import { FunctionComponent } from 'react';
import SINERGISE_LOGO from '../../images/logo.png';
import CODEDE_LOGO from '../../images/logo_codede.png';
import { CONFIG } from '../../meta/types/config';
import { BillingCODEDE, BillingCreo } from './extBillingPages';
import CREODIAS_LOGO from '../../images/logo_creodias.png';
import { Dataset } from '../../meta/types/wms/Dataset';
import {
  filterCodeDEDatasets,
  filterCreoDatasets,
  filterSgsDatasets,
  getServiceEndpoint,
  CODEDE_COLLECTIONS,
  CREODIAS_COLLECTIONS,
} from './producerCollections';
import { DomainAccountWithDomain } from '../../meta/types/user';
import { getAccountTypeText } from '../../constants/accounts';
import {
  getSinergiseLayersWarnings,
  getCreoDiasWarnings,
  getCodeDeWarnings,
} from './layersWarnings';
import { Layer } from '../../meta/types/wms/Layer';
import BillingPage from '../../pages/Billing/BillingSinergise';
import { SH_SUPPORT_EMAIL } from '../../constants/paths/internal';
import { AWS_EU_CENTRAL_1, CODE_DE, CREODIAS, ByocCollectionsCache } from '../../meta/types/byoc';
import { getAuthParametersByProvider } from '.';

type HelpLink = {
  text: string;
  link: string;
};

export type Skin = {
  // General
  logo: string;
  getAuthParameters: () => { clientId: string; redirectUri: string };
  // Dashboard
  showSubscriptionPlans: boolean;
  helpLinks: HelpLink[];
  contactLink: string;

  // Configurations
  getServiceEndpoint: (layers: Layer[] | undefined) => string | undefined;
  filterDatasets: (datasets: Dataset[]) => Dataset[];
  templateUserId: string;
  templateDomainId: string;
  getLayersWarnings: (
    layers: Layer[],
    byocCollectionsCache: ByocCollectionsCache,
  ) => FunctionComponent[];
  // Collections

  // BYOC
  defaultByocLocation: string;

  // User settings
  accountType?: string;
  showValidTo: boolean;

  // Statistics

  // Billing
  extBillingPage: any;
};

const SINERGISE_HELP_LINKS = [
  { text: 'Documentation', link: 'https://docs.sentinel-hub.com' },
  { text: 'Forum', link: 'https://forum.sentinel-hub.com' },
];

const CODE_DE_HELP_LINK = 'https://code-de.org/help';

const CREODIAS_CONTACT_US_LINK = 'https://creodias.eu/contact-us';

export const SinergiseSkin = (shAcc?: DomainAccountWithDomain): Skin => {
  return {
    logo: SINERGISE_LOGO,
    showSubscriptionPlans: true,
    templateUserId: CONFIG.sinergiseTemplateUserId,
    templateDomainId: CONFIG.sinergiseTemplateDomainId,
    defaultByocLocation: AWS_EU_CENTRAL_1,
    accountType: getAccountTypeText(shAcc?.type),
    showValidTo: true,
    getServiceEndpoint: layers =>
      getServiceEndpoint(layers, undefined, CONFIG.wmsConfigurationServiceBasePath),
    helpLinks: SINERGISE_HELP_LINKS,
    filterDatasets: filterSgsDatasets,
    getLayersWarnings: getSinergiseLayersWarnings,
    extBillingPage: BillingPage,
    contactLink: `mailto:${SH_SUPPORT_EMAIL}`,
    getAuthParameters: getAuthParametersByProvider,
  };
};

const NonSinergiseSkin = (shAcc?: DomainAccountWithDomain): Skin => {
  return {
    ...SinergiseSkin(shAcc),
    showValidTo: false,
    showSubscriptionPlans: false,
  };
};

export const CodeDeSkin = (shAcc?: DomainAccountWithDomain): Skin => {
  return {
    ...NonSinergiseSkin(shAcc),
    logo: CODEDE_LOGO,
    templateUserId: CONFIG.codeDeTemplateUserId,
    templateDomainId: CONFIG.codeDeTemplateDomainId,
    defaultByocLocation: CODE_DE,
    accountType: 'CODE-DE Account',
    getServiceEndpoint: layers =>
      getServiceEndpoint(layers, CODEDE_COLLECTIONS, 'https://code-de.sentinel-hub.com'),
    helpLinks: [{ text: 'CODE-DE Help', link: CODE_DE_HELP_LINK }, ...SINERGISE_HELP_LINKS],
    filterDatasets: filterCodeDEDatasets,
    extBillingPage: BillingCODEDE,
    contactLink: CODE_DE_HELP_LINK,
    getLayersWarnings: getCodeDeWarnings,
  };
};

export const CreoDiasSkin = (shAcc?: DomainAccountWithDomain): Skin => {
  return {
    ...NonSinergiseSkin(shAcc),
    logo: CREODIAS_LOGO,
    templateUserId: CONFIG.creodiasTemplateUserId,
    templateDomainId: CONFIG.creodiasTemplateDomainId,
    defaultByocLocation: CREODIAS,
    accountType: 'CreoDIAS Account',
    getServiceEndpoint: layers =>
      getServiceEndpoint(layers, CREODIAS_COLLECTIONS, 'https://creodias.sentinel-hub.com'),
    helpLinks: [{ text: 'CreoDIAS Help', link: CREODIAS_CONTACT_US_LINK }, ...SINERGISE_HELP_LINKS],
    filterDatasets: filterCreoDatasets,
    extBillingPage: BillingCreo,
    contactLink: CREODIAS_CONTACT_US_LINK,
    getLayersWarnings: getCreoDiasWarnings,
  };
};
